import { VEHICLES, vehicleForLevel, xpForLevel, comboMult, COMBO_DECAY, THEME_COLORS } from "./config";

export function norm(a) { while (a > Math.PI) a -= Math.PI * 2; while (a < -Math.PI) a += Math.PI * 2; return a; }
export const rand = (a, b) => a + Math.random() * (b - a);

export class Vehicle {
  constructor(x, y, isPlayer, name, theme = "classic") {
    this.x = x; this.y = y; this.angle = rand(0, Math.PI * 2); this.speed = 0;
    this.isPlayer = isPlayer; this.name = name;
    this.level = 1; this.xp = 0; this.tier = 0;
    this.score = 0; this.kills = 0; this.objects = 0;
    this.combo = 0; this.comboTimer = 0; this.maxCombo = 0;
    this.matchCoins = 0;
    this.theme = theme;
    this.color = THEME_COLORS[theme] || VEHICLES[0].body;
    this.trailColor = this.color;
    this.control = { throttle: 0, steer: 0, boost: false, targetAngle: null };
    this.boostEnergy = VEHICLES[0].boostMax;
    this.boosting = false;
    this.trail = [];
    this.invuln = 1.2;
    this.alive = true;
    this.respawnTimer = 0;
    this.applyTier(VEHICLES[0], true);
    this.flashTimer = 0;
  }
  tierDef() { return VEHICLES[this.tier]; }
  applyTier(t, heal) {
    const ratio = this.maxHealth ? this.health / this.maxHealth : 1;
    this.radius = t.radius; this.smashPower = t.smashPower;
    this.maxHealth = t.maxHealth; this.boostMax = t.boostMax;
    this.maxSpeed = t.maxSpeed; this.accel = t.accel; this.turn = t.turn;
    this.health = heal ? t.maxHealth : Math.min(t.maxHealth, ratio * t.maxHealth + 40);
  }
  addXp(n) {
    this.xp += n;
    const res = { leveled: false, evolved: false, newTier: null };
    while (this.level < 30 && this.xp >= xpForLevel(this.level + 1)) {
      this.level++;
      res.leveled = true;
      this.maxHealth += 8; this.health = Math.min(this.maxHealth, this.health + 30);
      const nt = vehicleForLevel(this.level);
      if (nt.id !== this.tier) { this.tier = nt.id; this.applyTier(nt, true); res.evolved = true; res.newTier = nt; }
    }
    return res;
  }
  registerCombo() {
    this.combo++; this.comboTimer = COMBO_DECAY;
    if (this.combo > this.maxCombo) this.maxCombo = this.combo;
    return comboMult(this.combo);
  }
  update(dtf, dt, world) {
    if (!this.alive) { this.respawnTimer -= dt; return; }
    if (this.invuln > 0) this.invuln -= dt;
    if (this.flashTimer > 0) this.flashTimer -= dt;
    const c = this.control;
    let boostActive = c.boost && this.boostEnergy > 1;
    if (boostActive) { this.boostEnergy = Math.max(0, this.boostEnergy - 42 * dt); }
    else { this.boostEnergy = Math.min(this.boostMax, this.boostEnergy + 16 * dt); }
    this.boosting = boostActive;
    const maxS = this.maxSpeed * (boostActive ? 1.9 : 1);
    const target = maxS * c.throttle;
    // Snappier acceleration, with an extra kick while boosting for a punchy launch.
    const accelMult = boostActive ? 1.6 : 1.15;
    this.speed += (target - this.speed) * Math.min(1, this.accel * accelMult * dtf);
    const speedFrac = Math.min(1, Math.abs(this.speed) / this.maxSpeed);
    if (c.targetAngle != null) {
      const diff = norm(c.targetAngle - this.angle);
      const maxTurn = this.turn * dtf * 2.0;
      this.angle += Math.max(-maxTurn, Math.min(maxTurn, diff));
    } else {
      // Responsive at low speed with a touch of slide (drift) at high speed.
      const grip = 0.55 + 0.45 * speedFrac;
      this.angle += c.steer * this.turn * dtf * grip * 1.15 * (this.speed < -0.2 ? -1 : 1);
    }
    this.drifting = Math.abs(c.steer) > 0.55 && speedFrac > 0.65 && !boostActive;
    this.x += Math.cos(this.angle) * this.speed * dtf;
    this.y += Math.sin(this.angle) * this.speed * dtf;
    // bounds
    const m = this.radius;
    if (this.x < m) { this.x = m; this.speed *= 0.4; }
    if (this.y < m) { this.y = m; this.speed *= 0.4; }
    if (this.x > world.w - m) { this.x = world.w - m; this.speed *= 0.4; }
    if (this.y > world.h - m) { this.y = world.h - m; this.speed *= 0.4; }
    // combo decay
    if (this.combo > 0) { this.comboTimer -= dt; if (this.comboTimer <= 0) this.combo = 0; }
    // trail
    if (boostActive && Math.abs(this.speed) > 1) {
      this.trail.push({ x: this.x, y: this.y, life: 0.4 });
      if (this.trail.length > 18) this.trail.shift();
    }
    for (const p of this.trail) p.life -= dt;
    this.trail = this.trail.filter((p) => p.life > 0);
  }
  power() { return this.smashPower * (Math.abs(this.speed) + 1) * (this.boosting ? 1.6 : 1); }
}

export class Obstacle {
  constructor(x, y, key, def) {
    this.x = x; this.y = y; this.key = key; this.def = def;
    this.hp = def.hp; this.maxHp = def.hp; this.radius = def.radius;
    this.angle = rand(0, Math.PI * 2); this.shake = 0; this.dead = false;
    this.cx = 0; this.cy = 0;
  }
  hit(dmg) { this.hp -= dmg; this.shake = 0.25; if (this.hp <= 0) this.dead = true; }
}

export class Resource {
  constructor(x, y, xp, coins, score) {
    this.x = x; this.y = y; this.xp = xp; this.coins = coins; this.score = score;
    const a = rand(0, Math.PI * 2), s = rand(1.5, 4);
    this.vx = Math.cos(a) * s; this.vy = Math.sin(a) * s;
    this.life = 12; this.dead = false; this.pull = 0;
  }
  update(dtf, dt, player) {
    this.life -= dt; if (this.life <= 0) { this.dead = true; return; }
    const dx = player.x - this.x, dy = player.y - this.y;
    const d = Math.hypot(dx, dy) || 1;
    if (d < 190 && player.alive) {
      const f = (1 - d / 190) * 1.4 + 0.25;
      this.vx += (dx / d) * f; this.vy += (dy / d) * f;
    }
    this.vx *= 0.9; this.vy *= 0.9;
    this.x += this.vx * dtf; this.y += this.vy * dtf;
    if (d < player.radius + 14 && player.alive) this.dead = "collect";
  }
}

export class Particle {
  constructor(x, y, vx, vy, life, color, size, type) {
    this.x = x; this.y = y; this.vx = vx; this.vy = vy;
    this.life = life; this.max = life; this.color = color; this.size = size; this.type = type;
    this.rot = rand(0, 6.28); this.vr = rand(-0.3, 0.3);
  }
  update(dtf, dt) {
    this.life -= dt;
    this.x += this.vx * dtf; this.y += this.vy * dtf;
    this.vx *= 0.94; this.vy *= 0.94;
    if (this.type === "debris") this.vy += 0.05 * dtf;
    this.rot += this.vr * dtf;
  }
}

export class FloatText {
  constructor(x, y, text, color, size = 22) {
    this.x = x; this.y = y; this.text = text; this.color = color; this.size = size; this.life = 1;
  }
  update(dt) { this.life -= dt * 0.9; this.y -= 40 * dt; }
}
